import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Fade,
  Backdrop,
  useMediaQuery,
  Badge,
  Stack
} from "@mui/material";
import CustomTable, { Column } from "../../../components/Admin/Common/Table";
import Action, { ActionItem } from "../../../components/Admin/Common/Action";
import EventViewModal from "../../../components/Admin/Event/EventViewModal";
import EventEditModal from "../../../components/Admin/Event/EventEditModal";
import EventCreateModal from "../../../components/Admin/Event/EventCreateModal";
import { eventService } from "../../../services/api/eventService";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import { Event } from "../../../types/event";

// Define modal animation transition times
const MODAL_TRANSITION_DURATION = 400;

const AdminEvents: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { tokens } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<Event["status"] | "all">("all");
  const [modalTransitioning, setModalTransitioning] = useState(false);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      // Use authenticated admin endpoint with ID token when available
      const data = tokens?.idToken 
        ? await eventService.getEventsAsAdmin(tokens.idToken)
        : await eventService.getEvents();
        
      const formattedEvents = Array.isArray(data)
          ? data.map((event) => ({
              ...event,
              status: (event.status as Event["status"]) || "upcoming",
            location: event.location || "N/A", // Handle possible undefined values
            organizer: event.organizer || "Unknown", // Handle possible undefined values
            }))
        : [];
      setEvents(formattedEvents);
      setFilteredEvents(formattedEvents);
    } catch (error) {
      toast.error("Failed to load events.");
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshEvents = async () => {
    setRefreshing(true);
    await fetchEvents();
    setRefreshing(false);
    toast.success("Events refreshed");
  };

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Filter events when search term or status filter changes
  useEffect(() => {
    let result = [...events];
    
    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter(event => event.status === statusFilter);
    }
    
    // Apply search term filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(
        event => 
          event.title.toLowerCase().includes(lowercasedSearch) ||
          (event.location?.toLowerCase()?.includes(lowercasedSearch) ?? false) ||
          (event.organizer?.toLowerCase()?.includes(lowercasedSearch) ?? false)
      );
    }
    
    setFilteredEvents(result);
  }, [searchTerm, statusFilter, events]);

  // Handle opening and closing modals with animation
  const handleOpenModal = (modalType: 'view' | 'edit' | 'create', event: Event | null = null) => {
    setModalTransitioning(true);
    
    if (event) {
      setSelectedEvent(event);
    }
    
    switch (modalType) {
      case 'view':
        setViewModalOpen(true);
        break;
      case 'edit':
        setEditModalOpen(true);
        break;
      case 'create':
        setCreateModalOpen(true);
        break;
    }
    
    setTimeout(() => {
      setModalTransitioning(false);
    }, MODAL_TRANSITION_DURATION);
  };
  
  const handleCloseModal = (modalType: 'view' | 'edit' | 'create') => {
    setModalTransitioning(true);
    
    switch (modalType) {
      case 'view':
        setViewModalOpen(false);
        break;
      case 'edit':
        setEditModalOpen(false);
        break;
      case 'create':
        setCreateModalOpen(false);
        break;
    }
    
    setTimeout(() => {
      setModalTransitioning(false);
      setSelectedEvent(null);
    }, MODAL_TRANSITION_DURATION);
  };

  // Handle status update
  const handleStatusUpdate = async (id: string, newStatus: Event["status"]) => {
    if (!tokens?.idToken) {
      toast.error("User is not authenticated.");
      return;
    }

    setUpdatingStatus(true);
    try {
      await eventService.updateEvent(tokens.idToken, id, { status: newStatus });
      toast.success(`Event status changed to ${newStatus}`);
      // Re-fetch events after update
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update event status.");
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle event creation by re-fetching the events list
  const handleCreateEvent = (newEvent: Event) => {
    // Optionally, you can re-fetch or just append the new event.
    // Here we re-fetch to ensure data consistency.
    fetchEvents();
    handleCloseModal('create');
    toast.success("Event created successfully.");
  };

  // Handle event update by re-fetching the events list
  const handleEditEvent = () => {
    fetchEvents();
    handleCloseModal('edit');
    toast.success("Event updated successfully.");
  };

  // Count events by status
  const eventCounts = {
    total: events.length,
    upcoming: events.filter(e => e.status === "upcoming").length,
    completed: events.filter(e => e.status === "completed").length,
    cancelled: events.filter(e => e.status === "cancelled").length,
  };

  // Define event columns
  const columns: Column<Event>[] = [
    { label: "Title", key: "title" },
    { 
      label: "Date", 
      key: "date",
      format: (date) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonthIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          {date}
        </Box>
      )
    },
    { 
      label: "Location", 
      key: "location",
      format: (location) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOnIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
          {location || "N/A"}
        </Box>
      )
    },
    { 
      label: "Organizer", 
      key: "organizer",
      format: (organizer) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PersonIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
          {organizer || "Unknown"}
        </Box>
      )
    },
    {
      label: "Status",
      key: "status",
      format: (status) => {
        let color = "";
        let backgroundColor = "";
        let icon = null;
        
        switch(status) {
          case "cancelled":
            color = theme.palette.error.main;
            backgroundColor = theme.palette.error.light;
            icon = <CancelIcon fontSize="small" />;
            break;
          case "completed":
            color = theme.palette.text.secondary;
            backgroundColor = theme.palette.grey[200];
            icon = <EventAvailableIcon fontSize="small" />;
            break;
          default: // upcoming
            color = theme.palette.success.main;
            backgroundColor = theme.palette.success.light;
            icon = <MoreTimeIcon fontSize="small" />;
        }
        
        return (
          <Chip 
            icon={icon}
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            sx={{ 
              color, 
              backgroundColor,
              fontWeight: "medium",
              minWidth: 120,
              textAlign: "center"
            }}
            size="small"
          />
        );
      },
    },
  ];

  // Define status filter options
  const handleFilterByStatus = (status: Event["status"] | "all") => {
    setStatusFilter(status);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          boxShadow: `0 10px 20px rgba(0,0,0,0.12)`,
          color: "white",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Box sx={{ position: "absolute", top: 10, right: 10, opacity: 0.2, fontSize: 100 }}>
          <DashboardIcon sx={{ fontSize: 'inherit' }} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          <DonutLargeIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              Event Management
            </Typography>
            <Typography variant="body1">
              Create, monitor, and manage all organization events
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={800}>
            <Card elevation={3} sx={{ 
              borderRadius: 2, 
              height: "100%",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)"
              }
            }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography color="textSecondary" gutterBottom>
                    Total Events
                  </Typography>
                  <Badge badgeContent={eventCounts.total} color="primary" />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                  {eventCounts.total}
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1000}>
            <Card elevation={3} sx={{ 
              borderRadius: 2, 
              height: "100%", 
              borderLeft: `4px solid ${theme.palette.success.main}`,
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)"
              }
            }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography color="textSecondary" gutterBottom>
                    Upcoming Events
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${eventCounts.upcoming}/${eventCounts.total}`} 
                    color="success"
                  />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: "bold", color: theme.palette.success.main }}>
                  {eventCounts.upcoming}
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1200}>
            <Card elevation={3} sx={{ 
              borderRadius: 2, 
              height: "100%", 
              borderLeft: `4px solid ${theme.palette.grey[500]}`,
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)"
              }
            }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography color="textSecondary" gutterBottom>
                    Completed Events
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${eventCounts.completed}/${eventCounts.total}`} 
                    color="default"
                  />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: "bold", color: theme.palette.grey[600] }}>
                  {eventCounts.completed}
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1400}>
            <Card elevation={3} sx={{ 
              borderRadius: 2, 
              height: "100%", 
              borderLeft: `4px solid ${theme.palette.error.main}`,
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)"
              }
            }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography color="textSecondary" gutterBottom>
                    Cancelled Events
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${eventCounts.cancelled}/${eventCounts.total}`} 
                    color="error"
                  />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: "bold", color: theme.palette.error.main }}>
                  {eventCounts.cancelled}
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* Action Bar */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: "box-shadow 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 2px 12px rgba(0,0,0,0.12)"
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                overflowX: "auto", 
                pb: 1,
                "&::-webkit-scrollbar": {
                  height: 4
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: 2
                }
              }}
            >
              <Button 
                variant={statusFilter === "all" ? "contained" : "outlined"}
                onClick={() => handleFilterByStatus("all")}
                size="small"
                sx={{ 
                  borderRadius: 2,
                  minWidth: 80,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)"
                  }
                }}
              >
                All
              </Button>
              <Button 
                variant={statusFilter === "upcoming" ? "contained" : "outlined"}
                onClick={() => handleFilterByStatus("upcoming")}
                color="success"
                size="small"
                sx={{ 
                  borderRadius: 2,
                  minWidth: 80,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Upcoming
              </Button>
              <Button 
                variant={statusFilter === "completed" ? "contained" : "outlined"}
                onClick={() => handleFilterByStatus("completed")}
                color="inherit"
                size="small"
                sx={{ 
                  borderRadius: 2,
                  minWidth: 80,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Completed
              </Button>
              <Button 
                variant={statusFilter === "cancelled" ? "contained" : "outlined"}
                onClick={() => handleFilterByStatus("cancelled")}
                color="error"
                size="small"
                sx={{ 
                  borderRadius: 2,
                  minWidth: 80,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Cancelled
              </Button>
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Tooltip title="Refresh events">
                <IconButton 
                  onClick={refreshEvents} 
                  disabled={refreshing}
                  sx={{ 
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: "rotate(180deg)"
                    }
                  }}
                >
                  {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
                onClick={() => handleOpenModal('create')}
                sx={{ 
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)"
                  }
                }}
      >
        Create Event
      </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

      {/* Display Loading Indicator */}
      {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
        ) : filteredEvents.length === 0 ? (
          <Box textAlign="center" py={5}>
            <Typography variant="h6" color="textSecondary">
              No events found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try adjusting your search or filters
            </Typography>
            <Button 
              variant="outlined" 
              sx={{ mt: 2 }}
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Clear filters
            </Button>
          </Box>
        ) : (
          <Fade in={true} timeout={500}>
            <Box>
        <CustomTable<Event>
          columns={columns}
                data={filteredEvents}
          rowActions={(event) => {
            const actions: ActionItem[] = [
              {
                label: "View Details",
                icon: <VisibilityIcon />,
                      onClick: () => handleOpenModal('view', event),
              },
              {
                label: "Edit Event",
                icon: <EditIcon />,
                      onClick: () => handleOpenModal('edit', event),
              },
              event.status !== "completed" && {
                label: "Mark as Completed",
                icon: <EventAvailableIcon />,
                onClick: () => handleStatusUpdate(event.id, "completed"),
                disabled: updatingStatus,
              },
              event.status !== "cancelled" && {
                label: "Cancel Event",
                icon: <CancelIcon />,
                onClick: () => handleStatusUpdate(event.id, "cancelled"),
                disabled: updatingStatus,
              },
            ].filter(Boolean) as ActionItem[];

            return <Action actions={actions} />;
          }}
          page={0}
          rowsPerPage={10}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
        />
            </Box>
          </Fade>
      )}
      </Paper>

      {/* View Event Modal */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={viewModalOpen}
      >
        <Fade in={viewModalOpen && !modalTransitioning} timeout={MODAL_TRANSITION_DURATION}>
          <Box sx={{ 
            position: 'relative', 
            width: isMobile ? '90%' : '70%',
            maxWidth: 800,
          }}>
      {selectedEvent && viewModalOpen && (
        <EventViewModal
          open={viewModalOpen}
          event={selectedEvent}
                onClose={() => handleCloseModal('view')}
        />
      )}
          </Box>
        </Fade>
      </Backdrop>

      {/* Edit Event Modal */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={editModalOpen}
      >
        <Fade in={editModalOpen && !modalTransitioning} timeout={MODAL_TRANSITION_DURATION}>
          <Box sx={{ 
            position: 'relative', 
            width: isMobile ? '90%' : '70%',
            maxWidth: 800,
          }}>
      {selectedEvent && editModalOpen && (
        <EventEditModal
          open={editModalOpen}
          event={selectedEvent}
                onClose={() => handleCloseModal('edit')}
          onUpdate={handleEditEvent}
        />
      )}
          </Box>
        </Fade>
      </Backdrop>

      {/* Create Event Modal */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={createModalOpen}
      >
        <Fade in={createModalOpen && !modalTransitioning} timeout={MODAL_TRANSITION_DURATION}>
          <Box sx={{ 
            position: 'relative', 
            width: isMobile ? '90%' : '70%',
            maxWidth: 800,
          }}>
      {createModalOpen && (
        <EventCreateModal
          open={createModalOpen}
                onClose={() => handleCloseModal('create')}
          onCreate={handleCreateEvent}
        />
      )}
          </Box>
        </Fade>
      </Backdrop>
    </Box>
  );
};

export default AdminEvents;
